import { Slide as SlideData } from '@/types/slide';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { Box, Skeleton, styled, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { LessonEditorSlideActionMenu, MenuAction } from './LessonEditorSlideActionMenu';
import { Slide } from '@/components/slides/Slide';
import { useMedia } from '@/hooks/useMedia/useMedia';

const LessonEditorItemBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
}));

const LessonEditorSlideListContainer = styled(Box)(({ theme }) => [
    {
        padding: theme.spacing(1),
        flex: 1,
        backgroundColor: (theme.vars || theme).palette.grey[200],
        overflow: 'auto',
    },
    theme.applyStyles('dark', {
        backgroundColor: (theme.vars || theme).palette.grey[800],
    }),
]);

type SortableItemProps = {
    item: SlideData;
    index: number;
    loading: boolean;
    editable: boolean;
    onDuplicate?: (index: number) => void;
    onDelete?: (index: number) => void;
    selectedSlideIndex: number;
    onSlideSelectedHandler?: (index: number) => void;
};

const SortableItem: React.FC<SortableItemProps> = ({ item, index, loading, editable, onDuplicate, onDelete, selectedSlideIndex, onSlideSelectedHandler }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        opacity: isDragging ? 0.5 : undefined,
    };

    const handleMenuClicked = (action: MenuAction) => {
        switch (action) {
            case 'duplicate':
                onDuplicate?.(index);
                break;
            case 'delete':
                onDelete?.(index);
                break;
        }
    };

    // Memoize the Slide component to prevent unnecessary re-renders when reordering SortableItems
    const MemoizedSlide = useMemo(() => {
        return <Slide key={item.id} components={item.data} preview />;
    }, [item.data, item.id]);

    return (
        <LessonEditorItemBox
            ref={setNodeRef}
            style={style}
            display='flex'
            flexDirection='column'
            alignItems='center'
            sx={(theme) => {
                return {
                    backgroundColor: selectedSlideIndex === index ? `color-mix(in srgb, ${(theme.vars || theme).palette.primary.main}, transparent 60%)` : 'transparent',
                };
            }}
        >
            <Typography variant='body2' sx={{ width: '100%' }}>
                {index + 1}
            </Typography>
            <Box display='flex' alignItems='center'>
                <Box sx={{ pr: 1, display: 'flex', touchAction: 'none' }} {...attributes} {...listeners}>
                    <DragHandleIcon fontSize='small' sx={{ cursor: editable ? 'grab' : 'default' }} />
                </Box>
                <Box display='flex' alignItems='flex-start' sx={{ position: 'relative' }}>
                    <Box
                        sx={{
                            boxShadow: 4,
                            background: 'white',
                            display: 'flex',
                            justifyContent: 'center',
                            justifyItems: 'center',
                            width: 80,
                            height: 60,
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            onSlideSelectedHandler?.(index);
                        }}
                    >
                        <div style={{ flex: 1, justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                            <Box sx={{ aspectRatio: 4 / 3, width: '100%', maxWidth: 1024, maxHeight: 768, height: 'auto', position: 'relative' }}>
                                {!loading && item ? MemoizedSlide : <Skeleton variant='rectangular' height={'100%'} />}
                            </Box>
                        </div>
                    </Box>
                    <Box sx={{ pl: 1 }}>
                        <LessonEditorSlideActionMenu editable={editable} onMenuClicked={handleMenuClicked} />
                    </Box>
                </Box>
            </Box>
        </LessonEditorItemBox>
    );
};

type Props = {
    slides: SlideData[];
    loading: boolean;
    onReorder: (items: SlideData[]) => void;
    editable: boolean;
    onDuplicateSlide?: (index: number) => void;
    onDeleteSlide?: (index: number) => void;
    selectedSlideIndex: number;
    onSlideSelectedHandler?: (index: number) => void;
};

export const LessonEditorSlideList: React.FC<Props> = ({ slides, loading, onReorder, editable, onDuplicateSlide, onDeleteSlide, selectedSlideIndex, onSlideSelectedHandler }) => {
    const { isMobile } = useMedia();

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (active && over && active.id !== over.id) {
                const oldIndex = slides.findIndex((item) => item.id === active.id);
                const newIndex = slides.findIndex((item) => item.id === over.id);

                if (oldIndex !== undefined && newIndex !== undefined) {
                    const newItems = arrayMove(slides, oldIndex, newIndex);
                    onReorder(newItems);
                }
            }
        },
        [slides, onReorder],
    );

    return (
        <LessonEditorSlideListContainer>
            <DndContext onDragEnd={handleDragEnd}>
                <SortableContext items={slides.map((slide) => slide.id)} disabled={!editable}>
                    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column' }}>
                        {slides &&
                            slides.map((item, index) => (
                                <SortableItem
                                    key={item.id}
                                    item={item}
                                    index={index}
                                    loading={loading}
                                    editable={editable}
                                    onDuplicate={onDuplicateSlide}
                                    onDelete={onDeleteSlide}
                                    selectedSlideIndex={selectedSlideIndex}
                                    onSlideSelectedHandler={onSlideSelectedHandler}
                                />
                            ))}
                    </Box>
                </SortableContext>
            </DndContext>
        </LessonEditorSlideListContainer>
    );
};
